import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ModalController, Platform } from '@ionic/angular';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';
import { AnalyticsService } from '../smoothr-web-app-core/services/analytics/analytics.service';
import { Api } from '../smoothr-web-app-core/api/api';
import { NavigationService } from './services/navigation.service';
import { register } from 'swiper/element/bundle';
import { CookieConsentService } from './services/cookie.service';
import { InfoModalComponent } from './components/info-modal/info-modal.component';
import { RepositoryService } from 'src/smoothr-web-app-core/services/repository/repository.service';
import { PaymentStatus } from '../smoothr-web-app-core/models/PaymentStatus';
import Payment from '../smoothr-web-app-core/models/Payment';
import Order from '../smoothr-web-app-core/models/Order';
import { PaymentCancelledModalComponent } from './components/payment-cancelled-modal/payment-cancelled-modal.component';
import { PaymentModalResult } from '../smoothr-web-app-core/components/payment-modal/payment-modal.component';
import * as Sentry from '@sentry/angular';
import { sleep } from '../smoothr-web-app-core/utils/utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LangService } from './services/lang.service';
import { MapsUtils } from '../maps/utils/maps';

register();

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
	static largeScreen = false;
	static analyticsEnabled = false;
	static isWebApp = true;
	static isNativeApp = false;
	constructor(
		private router: Router,
		private analytics: AnalyticsService,
		platform: Platform,
		private repository: RepositoryService,
		private navigationService: NavigationService,
		private cookieService: CookieConsentService,
		public translate: TranslateService,
		private modalCtrl: ModalController,
		private snackBar: MatSnackBar,
		private langSvc: LangService
	) {
		MapsUtils.initAllVennues(this.repository.allVenues$);
		this.handleCookieConsent();

		console.log(environment.customerGroup + ' ' + environment.version);
		const favIcon: any = document.querySelector("link[rel*='icon']") || document.createElement('link');
		favIcon.type = 'image/x-icon';
		favIcon.rel = 'shortcut icon';
		favIcon.href = '/assets/dean_and_david/logo.png';
		document.getElementsByTagName('head')[0].appendChild(favIcon);
		document.title = translate.instant('index.title.' + environment.customerGroup);

		this.langSvc.init();
		platform.ready().then(() => {
			AppComponent.largeScreen = platform.width() >= 992;
			platform.resize.subscribe(() => {
				AppComponent.largeScreen = platform.width() >= 992;
			});
		});
		Api.isMaintenanceActive()
			.then(result => {
				if (result) {
					this.navigationService.maintenance();
				}
			})
			.catch(error => {
				console.log(error);
			});
		// this.memoryCheck();
		this.checkPayments();
	}

	async memoryCheck() {
		await sleep(10000);
		for (let i = 0; i < 10; i++) {
			try {
				const TEN_MB = i * 1024 * 1024; // 10 MB in bytes (UTF-8, ASCII-safe)

				// Create a 10 MB string
				const str = 'a'.repeat(TEN_MB);

				console.log(`${i + 1} MB saved successfully.`);
				localStorage.setItem('test', str);
			} catch (e) {
				console.error(e);
				this.snackBar.open(`Error while saving ${i} MB`, 'OK', { duration: 10000 });
				await sleep(10_000);
			}
		}
	}

	checkPayments() {
		this.repository.checkOpenPayment().then(async result => {
			if (!result) {
				console.log('No Payment open.', result);
				return;
			}
			console.log(result);
			console.log({
				paymentDone: result.payment?.status === PaymentStatus.done,
				orderIsPaid: result.order?.isPayed,
			});
			if (result.payment?.status === PaymentStatus.done || result.order?.isPayed) {
				Sentry.captureMessage(
					`checkPayments(). Redirected successfully to payment success after Payment was done ${result.order.code}/${result.order._id}`
				);
				console.log('Navigate to payment success.');
				await this.router.navigate(['/payment/success/', result.payment?._id ?? result.order?.paymentId], {
					queryParams: { order: result.payment?.orders[0] ?? result.order?._id },
				});
				return;
			}
			Sentry.captureMessage(
				`checkPayments(). Payment was not finalized ${result.order.code}/${result.order._id}, showing error to user`
			);
			this.showErrorPayment(result.payment, result.order);
		});
	}

	ngOnInit() {
		window.addEventListener('pageshow', event => {
			if (event.persisted) {
				console.log('This page was restored from the bfcache.');
				this.checkPayments();
			} else {
				console.log('This page was loaded normally.');
			}
		});
	}

	private handleCookieConsent() {
		try {
			this.cookieService.initializeCookieConsent();
		} catch (e) {}
	}

	async setAnalyticsEnabled(enabled: boolean) {
		await this.analytics.setAnalyticsCollectionEnabled(enabled);
	}

	openPage(value: string) {
		if (!value) {
			return;
		}
		this.router.navigate([value]);
	}
	async showErrorPayment(payment: Payment, order: Order) {
		const retryResponse = await PaymentCancelledModalComponent.show(this.modalCtrl);
		if (!retryResponse) {
			await InfoModalComponent.show(this.modalCtrl, {
				title: this.translate.instant('error_dialog.title'),
				info: this.translate.instant('error_dialog.info'),
				closeButton: this.translate.instant('error_dialog.closeButton'),
			});
			this.repository.payment.next(null);
			return;
		}
		const result = await this.repository.showPaymentModal(
			this.modalCtrl,
			this.repository._venue,
			order,
			this.repository._customerAuth.customer,
			payment.tip,
			false
		);
		if (result?.result === PaymentModalResult.SUCCESS) {
			this.repository.verifiedOrder.emit(order);
			this.repository.payment.emit(result.payment);
			await this.navigationService.paymentSuccess(order._id, result.payment._id);
			return;
		}
	}
}
